import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { SectionTitle, SegmentedControl } from '../../shared/components';
import { Table } from '../../shared/components/data-display';
import {
  AllianceRequest,
  getAllianceRequests,
  getSalesEnquiries,
  getWebsiteFeedback,
  SalesEnquiry,
  SubmissionDocument,
  SubmissionType,
  SUBMISSION_STATUSES,
  updateSubmissionStatus,
  WebsiteFeedback,
} from '../contact-submissions.service';

const TABS: { label: string; value: SubmissionType }[] = [
  { label: 'Sales Enquiries', value: 'enquiries' },
  { label: '3rd Party Alliance', value: 'alliances' },
  { label: 'Feedback', value: 'feedback' },
];

type Submission = SalesEnquiry | AllianceRequest | WebsiteFeedback;

const LOADERS: Record<SubmissionType, () => Promise<Submission[]>> = {
  enquiries: getSalesEnquiries,
  alliances: getAllianceRequests,
  feedback: getWebsiteFeedback,
};

/** Long free-text fields are truncated in the cell but readable in full on hover. */
function LongText({ value }: { value: string | null }) {
  if (!value) return <span className='unavailable-message'>Not Available</span>;
  return (
    <Tooltip title={value}>
      <Typography
        variant='body2'
        sx={{
          maxWidth: 320,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'left',
        }}
      >
        {value}
      </Typography>
    </Tooltip>
  );
}

function Documents({ documents }: { documents: SubmissionDocument[] }) {
  if (!documents?.length) return <span className='unavailable-message'>None</span>;
  return (
    <Stack gap={0.5} alignItems='center'>
      {documents.map((doc) => (
        <Link key={doc.id} href={doc.url} target='_blank' rel='noopener' variant='body2'>
          {doc.name || `File ${doc.id}`}
        </Link>
      ))}
    </Stack>
  );
}

/**
 * Submissions from the public Contact Us page.
 *
 * Job applications are not here — those already flow into the hiring module as
 * candidates. This screen covers the three forms that had no destination at all
 * until the `/api/website/{enquiry,alliance,feedback}` endpoints were added.
 */
export default function ContactSubmissionsPage() {
  const [tab, setTab] = useState<SubmissionType>('enquiries');
  const [rows, setRows] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSubmissions = useCallback(async (type: SubmissionType) => {
    setLoading(true);
    try {
      const data = await LOADERS[type]();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      toast.error(
        logError({
          message: 'Could not load contact submissions',
          error: err,
          code: 'GET_CONTACT_SUBMISSIONS_FAILED',
        })
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions(tab);
  }, [fetchSubmissions, tab]);

  const handleStatusChange = async (id: number, status: string) => {
    const previous = rows;
    // Optimistic: the dropdown should feel immediate, and a failure re-reads anyway.
    setRows((current) =>
      current.map((row) => (row.id === id ? ({ ...row, status } as Submission) : row))
    );
    try {
      await updateSubmissionStatus(tab, id, status);
      toast.success('Status updated');
    } catch (err) {
      setRows(previous);
      toast.error(
        logError({
          message: 'Could not update status',
          error: err,
          code: 'UPDATE_SUBMISSION_STATUS_FAILED',
        })
      );
    }
  };

  const statusColumn = {
    id: 'status',
    dataKey: 'status',
    label: 'Status',
    align: 'center' as const,
    renderCell: (status: string, row: Submission) => (
      <TextField
        select
        size='small'
        value={SUBMISSION_STATUSES.includes(status as never) ? status : 'New'}
        onChange={(e) => handleStatusChange(row.id, e.target.value)}
        sx={{ minWidth: 130 }}
      >
        {SUBMISSION_STATUSES.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    ),
  };

  const submittedColumn = {
    id: 'submitted_at',
    dataKey: 'submitted_at',
    label: 'Received',
    align: 'center' as const,
    sortable: true,
  };

  const documentsColumn = {
    id: 'documents',
    dataKey: 'documents',
    label: 'Attachments',
    align: 'center' as const,
    renderCell: (documents: SubmissionDocument[]) => <Documents documents={documents} />,
  };

  const contactColumns = [
    { id: 'email', dataKey: 'email', label: 'Email', align: 'left' as const },
    { id: 'contact', dataKey: 'contact', label: 'Contact', align: 'center' as const },
    { id: 'whatsapp', dataKey: 'whatsapp', label: 'WhatsApp', align: 'center' as const },
  ];

  const COLUMNS: Record<SubmissionType, any[]> = {
    enquiries: [
      { id: 'name', dataKey: 'name', label: 'Name', align: 'left' as const, sortable: true },
      ...contactColumns,
      {
        id: 'enquiry',
        dataKey: 'enquiry',
        label: 'Enquiry',
        align: 'left' as const,
        renderCell: (value: string) => <LongText value={value} />,
      },
      {
        id: 'contact_type',
        dataKey: 'contact_type',
        label: 'Contact Back Via',
        align: 'center' as const,
        renderCell: (value: string, row: SalesEnquiry) =>
          value === 'phone' && row.preferred_time ? `${value} (${row.preferred_time})` : value,
      },
      documentsColumn,
      submittedColumn,
      statusColumn,
    ],
    alliances: [
      {
        id: 'company_name',
        dataKey: 'company_name',
        label: 'Company',
        align: 'left' as const,
        sortable: true,
      },
      { id: 'poc', dataKey: 'poc', label: 'Point of Contact', align: 'left' as const },
      ...contactColumns,
      {
        id: 'website',
        dataKey: 'website',
        label: 'Website',
        align: 'left' as const,
        renderCell: (value: string) =>
          value ? (
            <Link href={value} target='_blank' rel='noopener' variant='body2'>
              {value}
            </Link>
          ) : (
            <span className='unavailable-message'>Not Available</span>
          ),
      },
      {
        id: 'business_type',
        dataKey: 'business_type',
        label: 'Business Type',
        align: 'left' as const,
      },
      {
        id: 'description',
        dataKey: 'description',
        label: 'Offering',
        align: 'left' as const,
        renderCell: (value: string) => <LongText value={value} />,
      },
      {
        id: 'office_address',
        dataKey: 'office_address',
        label: 'Office Address',
        align: 'left' as const,
        renderCell: (value: string) => <LongText value={value} />,
      },
      documentsColumn,
      submittedColumn,
      statusColumn,
    ],
    feedback: [
      { id: 'name', dataKey: 'name', label: 'Name', align: 'left' as const, sortable: true },
      ...contactColumns,
      {
        id: 'feedback_type',
        dataKey: 'feedback_type',
        label: 'Type',
        align: 'center' as const,
        sortable: true,
        renderCell: (value: string) => (
          <Stack direction='row' gap={0.5} justifyContent='center' alignItems='center'>
            <Icon
              icon={value === 'negative' ? 'mdi:thumb-down-outline' : 'mdi:thumb-up-outline'}
              color={value === 'negative' ? '#c62828' : '#2e7d32'}
            />
            <span>{value}</span>
          </Stack>
        ),
      },
      {
        id: 'feedback',
        dataKey: 'feedback',
        label: 'Feedback',
        align: 'left' as const,
        renderCell: (value: string) => <LongText value={value} />,
      },
      documentsColumn,
      submittedColumn,
      statusColumn,
    ],
  };

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <SectionTitle
        title='Contact Us Submissions'
        meta={
          <SegmentedControl
            options={TABS}
            value={tab}
            onChange={(value: SubmissionType) => setTab(value)}
            ariaLabel='Submission type'
          />
        }
      />
      <Table
        columns={COLUMNS[tab]}
        data={rows}
        loading={loading}
        resetSortKey={tab}
        emptyMessage='No submissions yet'
      />
    </Box>
  );
}
